import React, { useMemo } from 'react'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import { OtuDataTableView } from '../ProjectPage/OtuDataTableView.js'

// The chat's OTU table is the "top N most abundant" view: total reads (abundance) desc, the real table's default in
// both modes, seeded explicitly so a directive without a sort says so. `totalRawReads` is the UI column key.
const DEFAULT_SORT_FIELD = 'totalRawReads'
const DEFAULT_SORT_DIRECTION = 'DESC'

/**
 * Chat-canvas OTU table (G1 — OTU-table parity). Mounts the REAL production `OtuDataTableView` EMBEDDED,
 * with the conversation's scope FORCED from the directive (scopeType/scopeId — never the page URL) and its
 * initial view seeded from the directive: blast source, group mode, CLASSIC/SPIN (+ algorithm/atTime), and
 * sort. The user then gets the full table: sort, per-column filters, column visibility, pagination, blast/
 * group/SPIN switches, and CSV export.
 *
 * Scope is forced by prop (defence-in-depth): the embedded table never reads the route `:id`, so it always
 * fetches the conversation's order/project regardless of what page the chat is mounted on.
 *
 * Follow-up: the directive can express blast/group/SPIN/sort today, but NOT per-column filters or column
 * visibility (a later directive extension — add e.g. `tableFilters`/`tableColumns` to VizDirective and seed
 * them here as `filters`/`hiddenColumns` params). Those currently start at the real table's defaults, which
 * the user can still adjust interactively.
 */
export function ChatOtuTable({ directive }: { directive: VizDirective }) {
  const { scopeType, scopeId } = directive
  const type = scopeType === 'project' ? 'PROJECT' : 'ORDER'

  // Build the seed the real table reads on mount (same keys it parses from the URL in full-page mode).
  const initialParams = useMemo(() => {
    const p = new URLSearchParams()
    if (directive.blastSource) p.set('blastType', directive.blastSource)
    if (directive.groupMode) p.set('groupMode', directive.groupMode)
    if (directive.spinAlgorithm) {
      // SPIN table: one row per current SPIN. `viewMode=SPIN` also flips the table's userChoseMode flag so
      // it won't auto-toggle back to CLASSIC.
      p.set('viewMode', 'SPIN')
      p.set('spinAlgorithm', directive.spinAlgorithm)
      if (directive.spinAtTime) p.set('spinAtTime', directive.spinAtTime)
    }
    // Sort: directive.tableSort is "field,DIRECTION" (camelCase column key). Default to abundance desc.
    const [field, dir] = (directive.tableSort ?? '').split(',')
    p.set('sortField', field || DEFAULT_SORT_FIELD)
    p.set('sortDirection', dir === 'ASC' ? 'ASC' : DEFAULT_SORT_DIRECTION)
    return p
  }, [directive])

  return <OtuDataTableView type={type} embedded scopeId={scopeId} initialParams={initialParams} />
}
